import React, { useMemo, useCallback } from 'react';

import { AboutThisEventModal } from './AboutThisEventModal';
import type { StructuredContent } from '@/src/listings/types';
import { AboutThisEventEmbedded } from './AboutThisEventEmbedded';
import { StructuredModuleRenderer } from './StructuredModuleRenderer';
import { MEDIA_QUERIES } from '@/src/listings/components/_shared/constants';
import { useMediaQuery } from '@/src/listings/hooks';

export type AboutThisEventProps = {
  showFullEventOverview: boolean;
  structuredContent?: StructuredContent;
  handleCloseModal: () => void;
};

export const AboutThisEvent: React.FC<AboutThisEventProps> = React.memo(
  ({ showFullEventOverview, structuredContent, handleCloseModal }) => {
    const isNarrowScreen = useMediaQuery(`(max-width: ${MEDIA_QUERIES.MOBILE_DRAWER_BREAKPOINT}px)`);

    const memoizedStructuredContent = useMemo(() => structuredContent, [structuredContent]);

    const renderModules = useCallback(
      (keyPrefix: string) =>
        memoizedStructuredContent?.modules?.map((module, index) => (
          <StructuredModuleRenderer key={`${keyPrefix}-${index}`} module={module} />
        )) ?? null,
      [memoizedStructuredContent?.modules],
    );

    const modalVisible = showFullEventOverview && isNarrowScreen;
    const shouldMountModal = isNarrowScreen || showFullEventOverview;

    const modalChildren = useMemo(
      () => (shouldMountModal ? renderModules('modal') : null),
      [shouldMountModal, renderModules],
    );

    const embeddedChildren = useMemo(
      () => (showFullEventOverview && !isNarrowScreen ? renderModules('embedded') : null),
      [showFullEventOverview, isNarrowScreen, renderModules],
    );

    // Never unmount the modal while it is open: stay mounted on the narrow path while collapsed so
    // Marmalade can play the isShown={false} exit animation and release scroll lock/focus, and stay
    // mounted while expanded even after the viewport crosses to wide so Radix can run its close path.
    const aboutThisEventModal = shouldMountModal ? (
      <AboutThisEventModal visible={modalVisible} onClose={handleCloseModal}>
        {modalChildren}
      </AboutThisEventModal>
    ) : null;

    const aboutThisEventEmbedded =
      !isNarrowScreen && showFullEventOverview ? (
        <AboutThisEventEmbedded visible>{embeddedChildren}</AboutThisEventEmbedded>
      ) : null;

    return (
      <>
        {aboutThisEventModal}
        {aboutThisEventEmbedded}
      </>
    );
  },
);

AboutThisEvent.displayName = 'AboutThisEvent';
